export type SectionId =
  | "opening"
  | "rising-risk"
  | "regulatory-imperative"
  | "trust-problem"
  | "bom-foundations"
  | "ecosystem"
  | "invitation";

export type SourceStat = {
  id: string;
  value: string;
  source: string;
};

export type NarrativeSection = {
  id: SectionId;
  eyebrow: string;
  headline: string;
  dek?: string;
  body: string[];
  stats?: SourceStat[];
};

export const sections: NarrativeSection[] = [
  {
    id: "opening",
    eyebrow: "Foundations of Risk",
    headline:
      "For most of the past 45 years, Australia has ranked second only to the United States for extreme-weather losses per capita.",
    dek:
      "A Bureau of Meteorology perspective on the data foundations of climate risk for Australian insurers and banks.",
    body: [
      "The question is no longer whether the risk is visible. It is how well Australia can see it coming, explain it, audit it, and act on it."
    ],
    stats: [
      {
        id: "per-capita",
        value: "2nd per capita over 45 years",
        source:
          "Insurance Council of Australia, Catastrophe Resilience Report 2024-25 / Insurance Industry Snapshot 2025, drawing on Munich Re NatCatSERVICE."
      }
    ]
  },
  {
    id: "rising-risk",
    eyebrow: "The Rising Risk",
    headline: "The losses are compounding.",
    body: [
      "Insured catastrophe losses over the last five years total about A$22.5 billion: an average of A$4.5 billion a year, a 67% increase on the prior five years.",
      "Extreme-weather losses have risen from 0.25% of GDP in the late 1990s to almost 0.75% of GDP in the first half of the 2020s.",
      "APRA's Insurance Climate Vulnerability Assessment projects expected national weather losses rising from less than A$7 billion in 2024 to over A$16 billion, approximately A$16.3 billion annually by 2050, under its higher-physical-risk scenario. It is a stress test, not a forecast."
    ],
    stats: [
      {
        id: "cat-losses",
        value: "A$22.5bn / A$4.5bn per year / +67%",
        source: "ICA Catastrophe Resilience Report 2024-25 / Insurance Industry Snapshot 2025."
      },
      {
        id: "apra-losses",
        value: "<A$7bn to >A$16bn by 2050",
        source: "APRA Insurance Climate Vulnerability Assessment, Mind the Gap."
      }
    ]
  },
  {
    id: "regulatory-imperative",
    eyebrow: "The Regulatory Imperative",
    headline: "Disclosure is now the law. Data is now infrastructure.",
    body: [
      "From reporting periods beginning on or after 1 January 2025, mandatory climate-related financial disclosure under AASB S2 began phasing in for the largest entities, with Groups 2 and 3 to follow on 1 July 2026 and 1 July 2027.",
      "APRA's CPG 229 sets prudential expectations for governance, risk management, scenario analysis and disclosure. APRA's Climate Vulnerability Assessments stress-test the system.",
      "The combined effect is a standing, auditable need for authoritative, nationally consistent hazard and climate data."
    ],
    stats: [
      {
        id: "aasb-s2",
        value: "1 Jan 2025 / 1 Jul 2026 / 1 Jul 2027",
        source: "AASB S2 and Treasury Laws Amendment Act 2024."
      }
    ]
  },
  {
    id: "trust-problem",
    eyebrow: "The Trust Problem",
    headline: "When two models disagree about your house, who do you believe?",
    body: [
      "A capable private market serves this demand, including Moody's RMS, Verisk, Risk Frontiers, Reask, XDI / Climate Valuation, and Jupiter Intelligence.",
      "Independent comparison by CarbonPlan found materially different risk scores for the same assets: across 128 California locations, by 2100 fire risk increased in 30% of locations for Jupiter and 33% for XDI, but in only 12% of locations for both.",
      "Most of these models are built on the same public foundation: BoM and Geoscience Australia data. The differentiator is whose foundation is authoritative."
    ],
    stats: [
      {
        id: "carbonplan",
        value: "30% Jupiter / 33% XDI / 12% both",
        source: "CarbonPlan, 2024 independent comparison of climate-risk scores."
      }
    ]
  },
  {
    id: "bom-foundations",
    eyebrow: "BoM's Foundations",
    headline: "You can't model what you never measured.",
    body: [
      "BoM's national observation network includes over 650 automatic weather stations, radars, ocean buoys, river gauges, and a network that extends across land, sea, sky and space, underpinned by around 6,000 volunteer rainfall observers.",
      "BARRA2 is the Bureau's high-resolution regional atmospheric reanalysis: nested in ERA5, at 12 km across the Australasian domain with a finer 4.4 km grid over Australia, spanning 1979 to near-present, the 46-year period 1979-2024.",
      "Operational numerical weather prediction and ensembles, including ACCESS run four times daily, sit beside BoM's statutory authority and public trust. BoM's official cyclone observations are the legal trigger for Australia's cyclone reinsurance pool."
    ],
    stats: [
      {
        id: "stations",
        value: "650+ AWS / ~6,000 observers",
        source: "Bureau of Meteorology network descriptions."
      },
      {
        id: "barra",
        value: "12 km / 4.4 km / 1979-2024 / 22-member ensemble",
        source: "BoM BARRA2 and BARRA-RE2 documentation."
      }
    ]
  },
  {
    id: "ecosystem",
    eyebrow: "The Web of the Ecosystem",
    headline: "Everyone builds on the same ground truth.",
    body: [
      "BoM and Geoscience Australia foundational data supports cat modellers and climate analytics firms, which support insurers' pricing, reinsurance and capital decisions, banks' mortgage, credit and disclosure decisions, ARPC's cyclone pool, and the regulatory backbone.",
      "BoM's role is to make the foundation open, authoritative and consistent, so the private market competes on models and service, not on whose raw data is right.",
      "BoM is also a partner, with CSIRO, the ABS and Geoscience Australia, in the Australian Climate Service that delivered Australia's first National Climate Risk Assessment."
    ]
  },
  {
    id: "invitation",
    eyebrow: "The Offer and The Invitation",
    headline: "A trusted foundation. And an official referee.",
    body: [
      "Four concept offerings frame the invitation: a National Hazard Reference Layer, a BARRA2 disclosure-grade reference for AASB S2 scenario work, an Official Event Parameter and Certification Service, and cost-recovered decision-grade delivery plus collaborative research.",
      "The proposition is deliberately enabling. BoM enables the market; it does not compete with it."
    ]
  }
];
